import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { User, Search, MapPin, Loader2, Clock, DollarSign } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function FreelancersListPage() {
  const [freelancers, setFreelancers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  useEffect(() => {
    loadFreelancers();
  }, []);

  const loadFreelancers = async (q = "") => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set("search", q);
      const res = await axios.get(`${API}/freelancers?${params}`);
      setFreelancers(res.data);
    } catch {
      setFreelancers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    loadFreelancers(search);
  };

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      {/* Hero */}
      <div className="bg-[#1B4F72] text-white py-12 md:py-16">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <h1 className="text-3xl md:text-4xl font-bold font-serif mb-3">Freelancer Tasarımcılar</h1>
          <p className="text-white/80 max-w-xl mx-auto">
            3D tasarımcılar, stand görselcileri, render uzmanları. Projeniz için yetenekli freelancerları bulun.
          </p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Search */}
        <form onSubmit={handleSearch} className="flex gap-2 mb-8 max-w-md mx-auto">
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="İsim, yetenek veya şehir ara..."
            className="flex-1"
          />
          <Button type="submit" className="bg-[#1B4F72] hover:bg-[#154360] gap-1.5">
            <Search className="w-4 h-4" /> Ara
          </Button>
        </form>

        {/* Results */}
        {loading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-stone-300" />
          </div>
        ) : freelancers.length === 0 ? (
          <div className="text-center py-16">
            <User className="w-16 h-16 text-stone-300 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-stone-700 mb-2">Freelancer Bulunamadı</h2>
            <p className="text-stone-500 mb-6">Bu kriterlere uygun freelancer henüz kayıt olmamış.</p>
            <Link to="/freelancer-giris">
              <Button className="bg-[#1B4F72] hover:bg-[#154360]">Freelancer Olarak Kayıt Ol</Button>
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {freelancers.map((freelancer) => (
              <Link
                key={freelancer.slug}
                to={`/freelancer/${freelancer.slug}`}
                className="bg-white rounded-xl border border-stone-200 p-5 hover:shadow-md hover:border-[#1B4F72]/30 transition-all group"
              >
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 rounded-full bg-stone-100 flex items-center justify-center flex-shrink-0 overflow-hidden">
                    {freelancer.profile_image ? (
                      <img src={freelancer.profile_image} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <User className="w-6 h-6 text-stone-400" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-stone-900 group-hover:text-[#1B4F72] transition-colors truncate">
                      {freelancer.full_name}
                    </h3>
                    <p className="text-xs text-[#1B4F72] font-medium">{freelancer.title}</p>
                    <div className="flex flex-wrap gap-2 mt-2 text-[10px] text-stone-500">
                      {freelancer.city && (
                        <span className="flex items-center gap-0.5">
                          <MapPin className="w-3 h-3" /> {freelancer.city}
                        </span>
                      )}
                      {freelancer.experience_years > 0 && (
                        <span className="flex items-center gap-0.5">
                          <Clock className="w-3 h-3" /> {freelancer.experience_years} yıl
                        </span>
                      )}
                      {freelancer.hourly_rate && (
                        <span className="flex items-center gap-0.5">
                          <DollarSign className="w-3 h-3" /> {freelancer.hourly_rate}
                        </span>
                      )}
                    </div>
                    {freelancer.skills?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {freelancer.skills.slice(0, 3).map((skill, i) => (
                          <Badge key={i} variant="outline" className="text-[9px]">{skill}</Badge>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}

        {/* CTA */}
        <div className="mt-12 bg-[#1B4F72] text-white rounded-xl p-8 text-center">
          <h2 className="text-xl font-bold font-serif mb-2">Freelancer Olun</h2>
          <p className="text-white/80 mb-4">Portfolyonuzu sergileyin, yeteneklerinizi tanıtın, yeni projelere ulaşın.</p>
          <Link to="/freelancer-giris">
            <Button variant="secondary" className="bg-white text-[#1B4F72] hover:bg-stone-100">
              KAYIT OL
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}
